import { formatUsd, type BillingCadence, type BillingDiscount } from "./billing-money";
import type {
  BillingInvoiceStatus,
  BillingQuoteStatus,
  BillingSubscriptionStatus,
  BillingTermsStatus,
} from "./billing-schemas";

/** The words the operations console uses for each status (ADR-0073, ADR-0075). */

export const STRIPE_STATUS_LABEL: Record<BillingInvoiceStatus, string> = {
  issuing: "Issuing",
  open: "Awaiting payment",
  processing: "Payment processing",
  past_due: "Past due",
  paid: "Paid",
  void: "Void",
  uncollectible: "Uncollectible",
};

export const SUBSCRIPTION_STATUS_LABEL: Record<BillingSubscriptionStatus, string> = {
  creating: "Creating",
  incomplete: "Awaiting first payment",
  active: "Active",
  past_due: "Past due",
  unpaid: "Unpaid",
  paused: "Paused",
  canceled: "Canceled",
};

export const TERMS_STATUS_LABEL: Record<BillingTermsStatus, string> = {
  draft: "Draft",
  approved: "Active",
  superseded: "Superseded",
  ended: "Ended",
  canceled: "Canceled",
};

/**
 * One list for quotes and the terms they become (ADR-0077): a quote's own states until it is
 * decided, then the states of the terms it became; terms approved without a quote use the same words.
 */
export type BillingAgreementStatus =
  | "draft"
  | "sent"
  | "expired"
  | "active"
  | "declined"
  | "superseded"
  | "ended"
  | "canceled";

export const AGREEMENT_STATUS_LABEL: Record<BillingAgreementStatus, string> = {
  draft: "Draft",
  sent: "Awaiting the customer",
  expired: "Expired",
  active: "Active",
  declined: "Declined",
  superseded: "Superseded",
  ended: "Ended",
  canceled: "Canceled",
};

export const APPROVED_INTERNALLY_LABEL = "Approved internally";

export const QUOTE_STATUS_LABEL: Record<BillingQuoteStatus, string> = {
  draft: "Draft",
  sent: "Awaiting the customer",
  accepted: "Accepted",
  declined: "Declined",
  expired: "Expired",
  superseded: "Superseded",
  canceled: "Canceled",
};

/** How often a line is charged, in the words both the console and a quote on paper use. */
export function cadenceLabel(cadence: BillingCadence | null): string {
  return cadence === "year" ? "per year" : cadence === "month" ? "per month" : "once";
}

/** "12.5 % off" or "$250.00 off"; the empty string when nothing comes off. */
export function describeDiscount(discount: BillingDiscount | null): string {
  if (discount === null) return "";
  return discount.type === "percent"
    ? `${(discount.basisPoints / 100).toFixed(2).replace(/\.?0+$/, "")} % off`
    : `${formatUsd(discount.amountMinor)} off`;
}
